import {Component, DestroyRef, HostListener, inject, OnDestroy, OnInit} from '@angular/core';
import {Location} from '@angular/common';
import {HttpErrorResponse} from '@angular/common/http';
import {ActivatedRoute, ActivatedRouteSnapshot, RouterStateSnapshot} from '@angular/router';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {Actions, ofType} from '@ngrx/effects';
import {Store} from '@ngrx/store';
import {TranslateService} from '@ngx-translate/core';
import {v4 as uuid} from 'uuid';
import {MessageService} from 'primeng/api';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {ClipboardModule} from '@ag-grid-enterprise/clipboard';
import {RangeSelectionModule} from '@ag-grid-enterprise/range-selection';
import {merge} from 'rxjs';
import {filter, map, take} from 'rxjs/operators';
import {
  CellClassParams, CellValueChangedEvent, ColDef, EditableCallbackParams, GridApi, GridOptions, GridReadyEvent,
  FillOperationParams, ICellEditorParams, ICellRendererParams, ProcessCellForExportParams, ProcessDataFromClipboardParams, ValueSetterParams
} from 'ag-grid-community';
import {DateUtils} from '../../../common/utils/date';
import {NumberUtils} from '../../../common/utils/number.utils';
import {DialogOptions} from '../../../common/dialog-options';
import {DebounceUtil} from '../../../helpers/debounce';
import {CanComponentDeactivate} from '../../../helpers/can-deactivate-guard.service';
import {WfmModalComponent as modalComponent} from '../../../components/wfm-modal/wfm-modal.component';
import {NumericEditorComponent} from '../common/cell-editors/numeric/numeric-editor.component';
import {SelectItem} from '../../../components/wfm-select/wfm-select.component';
import {EspNoRowsOverlayComponent} from '../../../components/no-rows-overlay/no-rows-overlay.component';
import {TenantService} from '../../../tenant.service';
import {currentPlanDetailSelector} from '../../../store';
import {
  AppActionTypes, DiscardExternalForecast, DiscardExternalForecastFailed, DiscardExternalForecastSuccess, EditExternalForecast, EditExternalForecastFailed, EditExternalForecastSuccess,
  ExternalForecastYearSettingsChanged, GetExternalForecast, GetExternalForecastFailed, GetExternalForecastSuccess, ReturnToPlanExternalForecast
} from '../../../store/actions';
import {
  AvailableYear, CellMeta, ExternalForecastPeriod, ExternalForecastResponse, ExternalForecastSettings, ExternalForecastYear
} from './external-forecast.model';
import {balanceCtMonth, buildEditRequest, CellChange, ctMonthTotal, isCtMonthValid, isOpenCt, isValidValue} from './external-forecast.edit';
import {cellMetaOf, cellStyleClasses, isEditableCell} from './external-forecast.cells';
import {buildRows, ComparisonRow, ctComparator, ctLabel, PINNED_ROW_COUNT} from './external-forecast.rows';
import {formatCount, formatPercent, toIntlLocale, truncateDecimals} from './external-forecast.utils';
import {CtTotalIndicatorComponent} from './ct-total-indicator/ct-total-indicator.component';

/** Month and total columns keep this width; spare grid width stays blank rather than being shared out. */
const MONTH_COL_WIDTH = 110;
const ROW_HEADER_COL_ID = "rowHeader";
const TOTAL_COL_ID = "yearTotal";
const AVERAGE_COL_ID = "yearAverage";
const DARK_HEADER_CLASS = "ef-dark-header";
const SUMMARY_CELL_CLASS = "ef-summary-cell";
const LAST_MONTH_CELL_CLASS = "ef-last-month";
/** Shared grid.scss class that makes a cell's text blue, as for Plan Detail's calculated cells. */
const CALCULATED_CELL_CLASS = "calculated-field";
/** Parameter column width, as on Plan Detail. */
const PARAMETER_COL_WIDTH = 225;
/** Changes made within this many ms (a paste, a range fill, quick typing) go out as one Edit, as on Plan Detail. */
const EDIT_BATCH_MS = 100;
/** Decimals the numeric editor keeps, as the backend stores them. */
const EDIT_DECIMALS = 2;
/** ag-grid's CellValueChangedEvent.source for a value written by dragging the fill handle. */
const FILL_SOURCE = "rangeService";

/** The value ag-grid hands the valueSetter, as a cell value: blank is null, anything not a number is NaN (invalid). */
function toCellValue(value: unknown): number | null {
  if (value === null || value === undefined || (typeof value === "string" && value.trim() === "")) {
    return null;
  }
  return typeof value === "number" ? value : NaN;
}

/** What the numeric editor validates: its text with "." as the decimal point; "" is blank, "-" is not a number. */
function editorTextValue(text: unknown): number | null {
  if (text === null || text === undefined || String(text).trim() === "") {
    return null;
  }
  return typeof text === "number" ? text : Number(text);
}

/** Identity of the CT ids a plan detail supplies; equal keys give identical labels. */
const ctIdKey = (ctInfo: {oid: string; id: string | number}[] | null | undefined): string =>
  (ctInfo ?? []).map(c => `${c.oid}:${c.id}`).join("|");

type PageState = "loading" | "ready";
export type YearType = "calendar" | "fiscal";
export type ForecastMode = "combine" | "replace";

/** Year dropdown items carry their availableYears entry as the value. */
const yearOf = (item: SelectItem): AvailableYear | undefined => item?.value as unknown as AvailableYear;

@Component({
  standalone: false,
  selector: 'esp-external-forecast',
  templateUrl: './external-forecast.component.html',
  styleUrl: './external-forecast.component.scss',
  providers: [MessageService]
})
export class ExternalForecastComponent implements OnInit, OnDestroy, CanComponentDeactivate {
  planId: string;
  /**
   * Plan Detail's saved state on entry (navigation state `saved`, kept in history), as CT Settings reads it; false
   * after this view's own year-settings change. Leaving the plan while false lets Plan Detail's unsaved dialog ask.
   */
  planDetailsSaved: boolean | undefined;
  planName: string;
  state: PageState = "loading";
  /** The response on screen: the last successful Retrieve or Edit. */
  response: ExternalForecastResponse | null = null;
  /** Cells whose recorded change failed validation, by cellId(); nothing is sent while any exist. */
  invalidCells = new Set<string>();
  /** One item per availableYears entry; value is the AvailableYear itself. */
  yearItems: SelectItem[] = [];
  selectedYearItem: SelectItem;
  colDefs: ColDef[] = [];
  /** Rows that scroll: everything beneath the two pinned summary rows, minus the children of collapsed groups. */
  rowData: ComparisonRow[] = [];
  /** Current Contacts Received and the first % Distribution: fixed above the scrolling rows. */
  pinnedRows: ComparisonRow[] = [];
  yearAnnouncement = "";
  /** The settings on screen: the last good response's, or the choice whose settings Edit is in flight. */
  yearType: YearType = "calendar";
  forecastMode: ForecastMode | null = null;
  monthItems: SelectItem[] = [];
  selectedMonthItem: SelectItem;
  /** The returned year has no forecast contacts: no rows, and the no-rows overlay says so. */
  noValidForecast = false;
  noRowsOverlayComponent = EspNoRowsOverlayComponent;
  noRowsOverlayParams = {getOverlayMsg: () => this.translate.instant("external.forecast.no.valid.forecast")};

  gridOptions: GridOptions = {
    defaultColDef: {
      editable: false,
      sortable: false,
      resizable: false,
      suppressMovable: true,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      width: MONTH_COL_WIDTH
    },
    // Row colour follows Plan Detail: white for editable parameters and group headers, grey for non-editable ones.
    getRowClass: params => [params.data?.editable || params.data?.groupKey ? "editable-row" : "readonly-row", params.data?.bold ? "ef-bold-row" : "",
      params.data?.groupStart ? "ef-group-start" : ""],
    // Typing and paste as on Plan Detail: range selection, the shared numeric editor, one Edit per 100 ms batch.
    enableRangeSelection: true,
    // Drag the corner of a selection along the months to extend its numeric pattern (as on Plan Detail), never down into other rows.
    enableFillHandle: true,
    fillHandleDirection: "x",
    fillOperation: (params: FillOperationParams) => this.fillOperation(params),
    components: {numericEditor: NumericEditorComponent},
    context: {
      isLoading: () => this.callInFlight,
      showMessage: (titleKey: string, msgKey: string, type: string) => this.showMessage(titleKey, msgKey, type),
      ctTotal: (period: string) => this.ctTotal(period),
      isCtMonthInvalid: (period: string) => this.invalidCtMonths.has(period),
      canBalanceCtMonth: (period: string) => this.invalidCtMonths.has(period) && this.balanceResult(period) !== null,
      balanceCtMonth: (period: string) => this.balanceMonth(period)
    },
    onCellValueChanged: (event: CellValueChangedEvent) => this.onCellValueChanged(event),
    processDataFromClipboard: (params: ProcessDataFromClipboardParams) => this.processDataFromClipboard(params),
    processCellFromClipboard: (params: ProcessCellForExportParams) => this.processCellFromClipboard(params),
    suppressRowClickSelection: true,
    enableBrowserTooltips: true,
    // Group header rows span the whole row, like the Plan Detail MU group headers, instead of using the column grid.
    isFullWidthRow: params => !!params.rowNode.data?.groupKey,
    fullWidthCellRenderer: (params: ICellRendererParams) => this.groupHeaderRenderer(params),
    onGridReady: (event: GridReadyEvent) => this.gridApi = event.api
  };

  /** Group header rows currently collapsed; all groups start expanded. */
  private collapsedGroups = new Set<string>();
  private allRows: ComparisonRow[] = [];
  readonly modules = [ClipboardModule, RangeSelectionModule];
  private gridApi: GridApi;
  private locale = toIntlLocale(null);
  /** date-fns locale for month names, following the app locale's language. */
  private dateLocale = DateUtils.getDatefnsLocale("en");
  /** The call this component has dispatched that has not completed yet. */
  private inFlight: "retrieve" | "edit" | "discard" | null = null;
  /** Id of this component's own Retrieve in flight; a result carrying another id is from an earlier visit or request. */
  private retrieveId: string | null = null;
  /** The settings change the Edit in flight carries, if it is one: a year-settings success marks the plan unsaved. */
  private pendingSettings: "calcMethod" | "yearSettings" | null = null;
  /** Changes recorded and not yet sent, by cellId(); a cell changed twice keeps its latest value. */
  private pendingChanges = new Map<string, CellChange>();
  /** Months whose changed CT split was held back because it does not total 100.00 (spec section 4 Q4). */
  private invalidCtMonths = new Set<string>();
  private readonly scheduleFlush = DebounceUtil.debounce(() => this.flushChanges(), EDIT_BATCH_MS);
  /** The view is gone: Return to plan has been sent, so nothing more may be recorded or sent. */
  private destroyed = false;
  /** The refresh flag has been read, by the first request. */
  private refreshChecked = false;
  /** The latest plan detail of this plan: its ctInfo supplies the CT integral ids for the row labels. */
  private planDetail: any = null;
  private readonly destroyRef = inject(DestroyRef);

  constructor(private route: ActivatedRoute,
              private store: Store<any>,
              private actions$: Actions,
              private location: Location,
              private translate: TranslateService,
              private messageService: MessageService,
              private tenantService: TenantService,
              private modalService: NgbModal) {
  }

  /** True while any call of this component is in flight; cells are not editable then. */
  get callInFlight(): boolean {
    return this.inFlight !== null;
  }

  /** A change has been recorded and not sent yet (inside the 100 ms batch, or held back by an invalid cell). */
  get editPending(): boolean {
    return this.pendingChanges.size > 0;
  }

  /** Controls that would replace the grid's data stay disabled until every change is sent and answered. */
  get controlsLocked(): boolean {
    return this.callInFlight || this.editPending || this.invalidCells.size > 0;
  }

  /**
   * The year selector, Calendar / Fiscal and the start month change the year shown: also locked while there are
   * unapplied changes, until Discard (or Apply) clears them (spec section 5 Q6).
   */
  get settingsLocked(): boolean {
    return this.controlsLocked || !!this.response?.hasUnappliedChanges;
  }

  /** Discard is for unapplied backend changes, or typed values the browser still holds (an invalid cell). */
  get canDiscard(): boolean {
    return !this.callInFlight && (!!this.response?.hasUnappliedChanges || this.invalidCells.size > 0);
  }

  ngOnInit() {
    this.planId = this.route.parent?.snapshot.paramMap.get("id") ?? this.route.snapshot.paramMap.get("id");
    this.planDetailsSaved = (this.location.getState() as any)?.saved;

    this.store.select("state").pipe(takeUntilDestroyed(this.destroyRef)).subscribe((state: any) => {
      this.planName = state?.currentPlanDetail?.planName;
      const locale = toIntlLocale(state?.locale);
      if (locale !== this.locale) {
        this.locale = locale;
        this.dateLocale = DateUtils.getDatefnsLocale(locale.split("-")[0]);
        this.render();
      }
    });
    this.translate.onLangChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.render());

    this.actions$.pipe(ofType(AppActionTypes.GetExternalForecastSuccess), takeUntilDestroyed(this.destroyRef))
      .subscribe((action: GetExternalForecastSuccess) => this.onLoaded(action.payload, action.requestId));
    this.actions$.pipe(ofType(AppActionTypes.GetExternalForecastFailed), takeUntilDestroyed(this.destroyRef))
      .subscribe((action: GetExternalForecastFailed) => this.onLoadFailed(action.payload, action.requestId));
    this.actions$.pipe(ofType(AppActionTypes.EditExternalForecastSuccess), takeUntilDestroyed(this.destroyRef))
      .subscribe((action: EditExternalForecastSuccess) => this.onEdited(action.payload));
    this.actions$.pipe(ofType(AppActionTypes.EditExternalForecastFailed), takeUntilDestroyed(this.destroyRef))
      .subscribe((action: EditExternalForecastFailed) => this.onEditFailed(action.payload));
    this.actions$.pipe(ofType(AppActionTypes.DiscardExternalForecastSuccess), takeUntilDestroyed(this.destroyRef))
      .subscribe((action: DiscardExternalForecastSuccess) => this.onDiscarded(action.payload));
    this.actions$.pipe(ofType(AppActionTypes.DiscardExternalForecastFailed), takeUntilDestroyed(this.destroyRef))
      .subscribe((action: DiscardExternalForecastFailed) => this.onDiscardFailed(action.payload));

    // The first Retrieve waits for Plan Detail to have this plan in the session cache (spec 5.3). The store may
    // still hold this plan from an earlier visit while the parent loads it again on a new session, so until the
    // first success each plan load of this plan retries the Retrieve (see planReady()).
    const isThisPlan = (planDetails: any) => planDetails?.planId === this.planId;
    merge(
      this.store.select(currentPlanDetailSelector).pipe(filter(isThisPlan), take(1)),
      this.actions$.pipe(
        ofType(AppActionTypes.PlanDetailReceivedV2, AppActionTypes.PlanDetailReceived),
        map((action: any) => action.payload),
        filter(isThisPlan))
    ).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((planDetails: any) => {
      const ctInfoChanged = ctIdKey(planDetails?.ctInfo) !== ctIdKey(this.planDetail?.ctInfo);
      this.planDetail = planDetails;
      this.planReady();
      // A plan detail whose CT ids differ, arriving after the rows are shown only refreshes their labels (no Retrieve).
      if (ctInfoChanged) {
        this.render();
      }
    });
  }

  /**
   * The view is gone because a navigation completed (never on reload or tab close): tell the backend the user returned
   * to the plan, fire-and-forget (spec section 7 Q8, 9.1 item 2). Not sent from canDeactivate, so a Cancel in Plan
   * Detail's own unsaved dialog after this guard allowed the exit keeps the overlay.
   */
  ngOnDestroy() {
    // A batch still waiting for its 100 ms, or an editor ag-grid would save while tearing down, must not follow
    // Return to plan as an Edit: both are dropped (the leave dialog has already asked about them).
    this.destroyed = true;
    if (this.gridApi && !this.gridApi.isDestroyed?.()) {
      this.gridApi.stopEditing(true);
    }
    if (this.planId) {
      this.store.dispatch(new ReturnToPlanExternalForecast({planId: this.planId}));
    }
  }

  /**
   * As CT Settings: unapplied changes, an invalid cell, or an edit not yet answered (recorded, or its Edit in flight)
   * ask first; Plan Detail asks when the plan itself is left unsaved.
   */
  async canDeactivate(component: CanComponentDeactivate, currentRoute: ActivatedRouteSnapshot, currentState: RouterStateSnapshot,
                      nextState: RouterStateSnapshot): Promise<boolean> {
    // let plan details parent page handle prompting for unsaved changes in place of unapplied changes (if any)
    if (this.isLeavingPlanDetailsWithUnsavedChanges(nextState)) {
      return true;
    }
    if (!this.response?.hasUnappliedChanges && this.invalidCells.size === 0 && !this.editPending && this.inFlight !== "edit") {
      return true;
    }
    const dgOption = new DialogOptions();
    dgOption.titleKey = "plan.exit.unapplied.dialog.title";
    dgOption.messageKey = "plan.exit.unapplied.dialog.msg";
    dgOption.msgType = "warn";
    dgOption.showCancel = true;
    dgOption.confirmLabel = "btn.confirm.label";
    try {
      await modalComponent.showModalMessage(dgOption, this.modalService);
      return true;
    } catch (e) {
      return false; // user chose to stay
    }
  }

  private isLeavingPlanDetailsWithUnsavedChanges(nextState: RouterStateSnapshot): boolean {
    return this.planDetailsSaved === false && !nextState?.url?.includes("site-plans");
  }

  /** The browser's native prompt while the backend holds unapplied changes (a reload keeps them; close loses them). */
  @HostListener("window:beforeunload", ["$event"])
  canCloseTab($event: any) {
    if (this.response?.hasUnappliedChanges) {
      $event.returnValue = true;
    }
  }

  /**
   * wfm-select emits the selected SelectItem itself (ng-select has no bindValue there). Only one Retrieve is in
   * flight at a time, and the year already shown is not requested again.
   */
  onYearSelected(selection: SelectItem) {
    this.afterEditorCommit(() => {
      const year = yearOf(selection);
      const sameYear = year?.startDate === yearOf(this.selectedYearItem)?.startDate;
      if (!year || this.state === "loading" || this.settingsLocked || sameYear) {
        return;
      }
      this.yearAnnouncement = year.label;
      this.retrieve({startDate: year.startDate, endDate: year.endDate});
    });
  }

  /**
   * Combine / Replace (spec section 5 Q5): a click on the inactive mode sends it at once as a settings-only Edit for
   * the year on screen; the active mode, and Replace while canReplace is false, do nothing.
   */
  onModeSelected(mode: ForecastMode) {
    this.afterEditorCommit(() => {
      if (!this.response || this.controlsLocked || mode === this.forecastMode || (mode === "replace" && !this.response.canReplace)) {
        return;
      }
      this.forecastMode = mode;
      this.sendSettings({calcMethod: mode === "combine" ? "COMBINED" : "REPLACED"}, "calcMethod");
    });
  }

  /** Calendar / Fiscal: a settings-only Edit; for Fiscal the backend keeps the current start month. */
  onYearTypeSelected(type: YearType) {
    this.afterEditorCommit(() => {
      if (!this.response || this.settingsLocked || type === this.yearType) {
        return;
      }
      this.yearType = type;
      this.sendSettings({yearMethod: type === "fiscal" ? "FISCAL" : "CALENDAR"}, "yearSettings");
    });
  }

  /** The fiscal start month (wfm-select emits the SelectItem; its value is the month number 1-12). */
  onStartMonthSelected(item: SelectItem) {
    this.afterEditorCommit(() => {
      const startMonth = item?.value as unknown as number;
      if (!this.response || this.settingsLocked || !startMonth || startMonth === this.response.settings.startMonth) {
        return;
      }
      this.selectedMonthItem = item;
      this.sendSettings({startMonth}, "yearSettings");
    });
  }

  /**
   * ag-grid leaves an editor open when a control outside the grid is clicked (stopEditingWhenCellsLoseFocus is off)
   * and would save it into the old rows when the response replaces them, as a stale change. A control that replaces
   * the grid's data saves it first instead: a changed value is recorded, so the lock check that follows refuses the
   * click and the value goes out in its own batch (spec 9.1 item 3).
   * ag-grid delivers cellValueChanged asynchronously (setTimeout 0 queued by stopEditing), so with an editor open the
   * action runs one tick later, after the change is recorded. With none open it runs at once.
   */
  private afterEditorCommit(action: () => void) {
    if (!this.gridApi || this.gridApi.getEditingCells().length === 0) {
      action();
      return;
    }
    this.gridApi.stopEditing();
    setTimeout(() => {
      if (!this.destroyed) {
        action();
      }
    }, 0);
  }

  /** Settings go through the same single Edit as cell changes, for the year on screen. */
  private sendSettings(settings: Partial<ExternalForecastSettings>, kind: "calcMethod" | "yearSettings") {
    const year = this.response.year;
    this.state = "loading";
    this.setNoValidForecast(false);
    this.inFlight = "edit";
    this.pendingSettings = kind;
    this.store.dispatch(new EditExternalForecast({planId: this.planId, startDate: year.startDate, endDate: year.endDate,
      request: {settings}}));
  }

  /** Exports exactly what the grid displays (collapsed groups stay collapsed), named after the current view. */
  exportCsv() {
    this.gridApi?.exportDataAsCsv({fileName: this.csvFileName()});
  }

  csvFileName(): string {
    const label = this.selectedYearItem?.label;
    const period = !label ? null
      : this.yearType === "fiscal"
        ? `${this.translate.instant("external.forecast.fiscal.year")} ${label} (${this.selectedMonthItem?.label})`
        : `${this.translate.instant("external.forecast.calendar.year")} ${label}`;
    const mode = this.forecastMode
      ? this.translate.instant(this.forecastMode === "combine" ? "external.forecast.combine.forecast" : "external.forecast.replace.forecast")
      : null;
    return [this.planName, this.translate.instant("external.forecast.title"), period, mode].filter(Boolean).join(" - ");
  }

  /**
   * This plan is loaded in Plan Detail: Retrieve, unless a year has already loaded or a request is in flight (year
   * selection is the only trigger after that). Only the very first request reads the refresh flag.
   */
  private planReady() {
    if (this.response || this.callInFlight) {
      return;
    }
    const refresh = !this.refreshChecked && this.takeRefreshFlag();
    this.refreshChecked = true;
    this.retrieve({refresh});
  }

  /** True on arrival from the Plan Detail menu; the flag is then cleared so a reload does not refresh (spec 2.3). */
  private takeRefreshFlag(): boolean {
    const state = this.location.getState() as any;
    if (state?.efRefresh !== true) {
      return false;
    }
    // Keep the router's own history keys (navigationId, ɵrouterPageId).
    const {efRefresh, ...rest} = state;
    this.location.replaceState(this.location.path(), "", rest);
    return true;
  }

  /**
   * ag-grid decides on its overlay only when the row data changes, so a change of the flag alone neither hides the
   * message that is up nor brings it back: the grid option and the overlay are set here, at once.
   */
  private setNoValidForecast(value: boolean) {
    this.noValidForecast = value;
    if (!this.gridApi || this.gridApi.isDestroyed?.()) {
      return;
    }
    this.gridApi.setGridOption("suppressNoRowsOverlay", !value);
    if (value) {
      this.gridApi.showNoRowsOverlay();
    } else {
      this.gridApi.hideOverlay();
    }
  }

  private retrieve(params: {startDate?: string; endDate?: string; refresh?: boolean}) {
    this.state = "loading";
    // ag-grid's no-rows overlay would sit above the loading panel; the response sets the flag again.
    this.setNoValidForecast(false);
    this.inFlight = "retrieve";
    this.retrieveId = uuid();
    this.store.dispatch(new GetExternalForecast({planId: this.planId, ...params, requestId: this.retrieveId}));
  }

  /**
   * The effect is shared, so a Retrieve for another plan, or one from an earlier visit to this plan (it is still
   * queued when the user leaves and comes back), can complete here: only the success of this component's own
   * Retrieve, matched by request id, counts.
   */
  private onLoaded(response: ExternalForecastResponse, requestId?: string) {
    if (this.inFlight !== "retrieve" || requestId !== this.retrieveId || response?.planId !== this.planId) {
      return;
    }
    this.showResponse(response);
  }

  /** Shows a successful Retrieve, Edit or Discard response; it becomes the last good response. */
  showResponse(response: ExternalForecastResponse) {
    this.inFlight = null;
    this.response = response;
    this.yearItems = response.availableYears.map(y => ({label: y.label, value: y as any, object: y}));
    this.selectedYearItem = this.yearItems.find(item => yearOf(item).startDate === response.year.startDate);
    this.yearType = response.settings.yearMethod === "FISCAL" ? "fiscal" : "calendar";
    this.forecastMode = response.settings.calcMethod === "COMBINED" ? "combine"
      : response.settings.calcMethod === "REPLACED" ? "replace" : null;
    this.noValidForecast = !response.year.validForecast;
    this.state = "ready";
    this.render();
  }

  /**
   * Every Retrieve failure shows the same toast, as in Staffing Distribution (spec 5.7). Before any year has loaded
   * the grid stays empty; a failed year change keeps the year already shown, and sends no further Retrieve.
   */
  private onLoadFailed(error: HttpErrorResponse, requestId?: string) {
    // Only a failure of this component's own Retrieve, matched by request id, counts.
    if (this.inFlight !== "retrieve" || requestId !== this.retrieveId) {
      return;
    }
    this.inFlight = null;
    this.state = "ready";
    if (this.response) {
      // wfm-select keeps the failed year in its own ngModel; a new reference re-binds its selectedItem, and ng-select
      // (bindLabel "label", no bindValue/compareWith) matches it to the loaded year's option by label.
      this.selectedYearItem = {...this.selectedYearItem};
      this.setNoValidForecast(!this.response.year.validForecast);
    } else {
      this.noValidForecast = false;
      this.allRows = [];
      this.applyGroupState();
    }
    this.messageService.add({severity: "error", detail: this.translate.instant("external.forecast.load.failed")});
  }

  /** Failed actions carry no planId; a failure whose URL names another plan is not this component's. */
  private isOwnFailure(error: HttpErrorResponse): boolean {
    return !error?.url || error.url.includes(this.planId);
  }

  /** Re-renders the last good response, dropping typed values, recorded changes and invalid marks. */
  restoreLastResponse() {
    this.pendingChanges.clear();
    this.invalidCells.clear();
    this.invalidCtMonths.clear();
    this.render();
    this.gridApi?.refreshCells({force: true});
  }

  /** ag-grid has written a typed or pasted value into the row (valueSetter): record it and restart the batch. */
  onCellValueChanged(event: CellValueChangedEvent) {
    const row: ComparisonRow = event.data;
    const colId = event.column?.getColId() ?? event.colDef?.colId;
    if (this.destroyed || !row || !colId) {
      return;
    }
    const period = colId === TOTAL_COL_ID ? null : colId;
    const value = period === null ? row.total : row.values[period];
    const change: CellChange = row.ctShare ? {period, ctOid: row.ctOid, value} : {period, field: row.field, value};
    if (event.source === "paste") {
      change.fromPaste = true;
    }
    if (event.source === FILL_SOURCE) {
      this.roundFilled(row, period, change);
    }
    this.pendingChanges.set(this.cellId(row, period), change);
    this.scheduleFlush();
  }

  /**
   * A drag onto the annual Total writes nothing (a yearly entry is typed or pasted there). A numeric pattern is extended by ag-grid
   * (its value reaches the cell as a number, see editColumn). Repeating the selection, and Alt's count by one, are done here so the
   * cell gets the raw number or blank: ag-grid would hand it the formatted text ("1,234.50"), which the valueSetter reads as invalid.
   */
  private fillOperation(params: FillOperationParams): any {
    if (params.column.getColId() === TOTAL_COL_ID) {
      return params.currentCellValue;
    }
    const values = params.values ?? [];
    if (values.length === 0) {
      return false;
    }
    const allNumbers = values.every(v => typeof v === "number" && isFinite(v));
    if (!params.event?.altKey && allNumbers) {
      return false;
    }
    if (allNumbers && params.initialValues.length === 1) {
      return (values[values.length - 1] as number) + (params.direction === "left" || params.direction === "up" ? -1 : 1);
    }
    return values[params.currentIndex % values.length] ?? null;
  }

  /** An extended pattern can run to many decimals; keep what the editor allows, and show the rounded value. */
  private roundFilled(row: ComparisonRow, period: string | null, change: CellChange) {
    if (typeof change.value !== "number" || !isFinite(change.value)) {
      return;
    }
    const factor = Math.pow(10, EDIT_DECIMALS);
    const rounded = Math.round(change.value * factor) / factor;
    if (rounded === change.value) {
      return;
    }
    change.value = rounded;
    if (period === null) {
      row.total = rounded;
    } else {
      row.values[period] = rounded;
    }
    this.gridApi?.refreshCells({force: true});
  }

  /** Identity of a value cell: "<rowKey>|<period or 'total'>"; rowKey is the cell key, or "ct:<oid>" for a CT share. */
  cellId(row: ComparisonRow, period: string | null): string {
    const rowKey = row.ctShare ? `ct:${row.ctOid}` : row.cellKey;
    return `${rowKey}|${period ?? "total"}`;
  }

  /**
   * End of a 100 ms batch (Plan Detail's updateGridValid): validate every recorded change against its cellMeta, and
   * each month with a changed CT share against the 100.00 total (Staffing Distribution); if any is invalid mark it and
   * send nothing, else send all of them as one Edit for the year on screen.
   */
  private flushChanges() {
    if (this.destroyed) {
      return;
    }
    // An editor still open commits first, so its value joins this batch rather than arriving while the call is in flight.
    this.afterEditorCommit(() => this.sendBatch());
  }

  private sendBatch() {
    if (!this.response || this.callInFlight || this.pendingChanges.size === 0) {
      return;
    }
    this.dropPastedTotalWithMonths();
    const year = this.response.year;
    this.invalidCells.clear();
    this.invalidCtMonths.clear();
    this.pendingChanges.forEach((change, id) => {
      const row = this.allRows.find(r => this.cellId(r, change.period) === id);
      const period = change.period === null ? null : year.periods.find(p => p.period === change.period);
      if (!row || period === undefined || !isValidValue(change.value, cellMetaOf(row, period, year))) {
        this.invalidCells.add(id);
      }
      if (change.ctOid !== undefined && period && !isCtMonthValid(this.ctTotal(period.period))) {
        this.invalidCtMonths.add(period.period);
      }
    });
    // Every open CT share of an invalid month is marked, so any of them can be corrected while the rest is locked.
    this.invalidCtMonths.forEach(month => year.periods.find(p => p.period === month)?.cts.filter(isOpenCt)
      .forEach(ct => this.invalidCells.add(`ct:${ct.ctOid}|${month}`)));
    if (this.invalidCells.size > 0) {
      this.gridApi?.refreshCells({force: true});
      return;
    }
    const request = buildEditRequest([...this.pendingChanges.values()], year, (period, ctOid) => this.ctShareValue(period, ctOid));
    this.pendingChanges.clear();
    this.gridApi?.refreshCells({force: true});
    if (!request) {
      return;
    }
    this.state = "loading";
    this.inFlight = "edit";
    this.store.dispatch(new EditExternalForecast({planId: this.planId, startDate: year.startDate, endDate: year.endDate, request}));
  }

  /**
   * A paste that also writes months of the Business Metric row ignores the value that landed on the annual Total, a number or a blank
   * (PO, 3 Nov 2026): the backend calculates the Total from the months. It is dropped before validation, so it cannot turn the cell
   * red, and the Total cell shows the server's value again. A paste that touches only the Total, and a typed Total, are still a yearly entry.
   */
  private dropPastedTotalWithMonths() {
    const changes = [...this.pendingChanges.entries()];
    const businessMetric = (change: CellChange) => change.fromPaste && change.field === "businessMetric";
    if (!changes.some(([, change]) => businessMetric(change) && change.period !== null)) {
      return;
    }
    const totals = changes.filter(([, change]) => businessMetric(change) && change.period === null);
    totals.forEach(([id]) => {
      this.pendingChanges.delete(id);
      const row = this.allRows.find(r => this.cellId(r, null) === id);
      if (row) {
        row.total = this.response.year.annual.businessMetric;
      }
    });
    if (totals.length > 0) {
      this.gridApi?.refreshCells({force: true});
    }
  }

  /** What Balance would give the month's CTs the planner did not change (the changed ones are the month's held batch), or null. */
  private balanceResult(period: string): Record<string, number> | null {
    const month = this.response?.year.periods.find(p => p.period === period);
    if (!month) {
      return null;
    }
    const open = month.cts.filter(isOpenCt).map(ct => ({ctOid: ct.ctOid, value: this.ctShareValue(period, ct.ctOid)}));
    const changed = new Set<string>();
    this.pendingChanges.forEach(change => change.period === period && change.ctOid !== undefined && changed.add(change.ctOid));
    return balanceCtMonth(open, changed);
  }

  /** Balance: the result goes in like typed values, so it is validated and sent as one Edit. */
  private balanceMonth(period: string) {
    const result = this.balanceResult(period);
    if (!result) {
      return;
    }
    Object.entries(result).forEach(([ctOid, value]) => {
      const row = this.allRows.find(r => r.ctShare && r.ctOid === ctOid);
      if (row) {
        row.values[period] = value;
        this.pendingChanges.set(this.cellId(row, period), {period, ctOid, value});
      }
    });
    this.scheduleFlush();
  }

  /** The CT share on screen (typed, or from the last response). */
  private ctShareValue(period: string, ctOid: string): number | null {
    return this.allRows.find(r => r.ctShare && r.ctOid === ctOid)?.values[period] ?? null;
  }

  /** The month's open-CT shares on screen summed to 2 decimals; NaN for a period not in the year on screen. */
  ctTotal(period: string): number {
    const month = this.response?.year.periods.find(p => p.period === period);
    return month ? ctMonthTotal(month, ctOid => this.ctShareValue(period, ctOid)) : NaN;
  }

  private onEdited(response: ExternalForecastResponse) {
    if (this.inFlight !== "edit" || response?.planId !== this.planId) {
      return;
    }
    const yearSettingsChanged = this.pendingSettings === "yearSettings";
    this.pendingSettings = null;
    this.showResponse(response);
    // Plan Detail marks the plan unsaved (spec section 7); a calcMethod change is not a plan change.
    if (yearSettingsChanged) {
      this.planDetailsSaved = false;
      this.store.dispatch(new ExternalForecastYearSettingsChanged({planId: this.planId}));
    }
  }

  /** The backend stores nothing when an Edit fails: put the grid back to the last good response (spec 8). */
  private onEditFailed(error: HttpErrorResponse) {
    if (this.inFlight !== "edit" || !this.isOwnFailure(error)) {
      return;
    }
    this.inFlight = null;
    this.state = "ready";
    if (this.pendingSettings) {
      // The controls show the last good response's settings again; new items make the selects drop the failed choice.
      this.pendingSettings = null;
      this.showResponse(this.response);
    } else {
      this.restoreLastResponse();
    }
    this.messageService.add({severity: "error", detail: this.translate.instant("external.forecast.edit.failed")});
  }

  /**
   * Discard (spec section 6 Q7), after a confirmation as in Staffing Distribution. With unapplied changes on the backend
   * the backend discards them; with only browser-side invalid cells nothing is sent (the backend would answer with the
   * default year) and the last response is restored locally.
   */
  async onDiscard() {
    if (!this.canDiscard) {
      return;
    }
    // An editor still open is cancelled: Discard drops it anyway, and saved later it would follow as a stray Edit.
    this.gridApi?.stopEditing(true);
    const dgOption = new DialogOptions();
    dgOption.titleKey = "external.forecast.discard.dlg.title";
    dgOption.messageKey = (this.translate.instant("external.forecast.discard.dlg.msg") as string)
      .replace("{0}", this.selectedYearItem?.label ?? "");
    dgOption.msgType = "warn";
    dgOption.showCancel = true;
    dgOption.confirmLabel = "btn.confirm.label";
    try {
      await modalComponent.showModalMessage(dgOption, this.modalService);
    } catch (e) {
      return; // user cancelled
    }
    // A batch saved just before the click may have gone out as an Edit while the dialog was open: discard anyway.
    // The effect queues the Discard after that Edit, whose result is then ignored (inFlight is no longer "edit").
    const editInFlight = this.inFlight === "edit";
    if (editInFlight) {
      this.pendingChanges.clear();
      this.invalidCells.clear();
      this.invalidCtMonths.clear();
      this.pendingSettings = null;
    } else if (!this.canDiscard) {
      return;
    }
    if (!editInFlight && !this.response.hasUnappliedChanges) {
      this.restoreLastResponse();
      return;
    }
    this.state = "loading";
    this.inFlight = "discard";
    this.store.dispatch(new DiscardExternalForecast({planId: this.planId}));
  }

  private onDiscarded(response: ExternalForecastResponse) {
    if (this.inFlight !== "discard" || response?.planId !== this.planId) {
      return;
    }
    this.pendingChanges.clear();
    this.invalidCells.clear();
    this.invalidCtMonths.clear();
    this.showResponse(response);
    this.gridApi?.refreshCells({force: true});
  }

  /** The grid stays as it was (recorded changes and invalid marks included). */
  private onDiscardFailed(error: HttpErrorResponse) {
    if (this.inFlight !== "discard" || !this.isOwnFailure(error)) {
      return;
    }
    this.inFlight = null;
    this.state = "ready";
    this.messageService.add({severity: "error", detail: this.translate.instant("external.forecast.discard.failed")});
  }

  /** Plan Detail's paste: drop Excel's trailing empty row and repeat the copied data over the selected range. */
  processDataFromClipboard(params: ProcessDataFromClipboardParams): string[][] {
    let data = params.data;
    const lastRow = data[data.length - 1];
    if (data.length > 1 && lastRow.length === 1 && lastRow[0] === "") {
      data.pop();
    }
    data = this.duplicateClipboardToSelection(data);
    return data;
  }

  processCellFromClipboard(params: ProcessCellForExportParams): any {
    const val = params.value;
    const num = NumberUtils.parseLocaleNumber(val, this.locale);
    return isNaN(num) ? val : num;
  }

  /** Plan Detail's workaround: ag-grid does not fill a selection larger than the copied data. */
  duplicateClipboardToSelection(data: string[][]): string[][] {
    const cellRanges = this.gridApi?.getCellRanges() ?? [];
    const maxDistance = cellRanges.reduce((max, cellRange) =>
      Math.max(cellRange.endRow.rowIndex - cellRange.startRow.rowIndex, max), 0);
    while (data.length > 0 && data.length <= maxDistance) {
      data = [...data, ...data];
    }
    return data;
  }

  /** The numeric editor's "Invalid Value" dialog, as on Plan Detail. */
  showMessage(titleKey: string, msgKey: string, type: string) {
    const dgOption = new DialogOptions();
    dgOption.titleKey = titleKey;
    dgOption.messageKey = msgKey;
    dgOption.msgType = type;
    dgOption.showCancel = false;
    dgOption.confirmLabel = "btn.ok.label";
    const dialog = modalComponent.showModalMessage(dgOption, this.modalService);
    dialog.finally(() => {
      const focusedCell = this.gridApi?.getFocusedCell();
      if (focusedCell) {
        this.gridApi.setFocusedCell(focusedCell.rowIndex, focusedCell.column, focusedCell.rowPinned);
      }
    });
    return dialog;
  }

  private render() {
    if (!this.response) {
      return;
    }
    const year = this.response.year;
    this.monthItems = Array.from({length: 12}, (_, i) => ({label: DateUtils.getMonthName(i + 1, this.dateLocale), value: i + 1 as any, object: null}));
    this.selectedMonthItem = this.monthItems[this.response.settings.startMonth - 1];
    this.colDefs = this.buildColumnDefs(year);
    this.allRows = this.noValidForecast ? [] : buildRows(year, key => this.translate.instant(key),
      ct => ctLabel(ct.ctOid, ct.ctName, this.planDetail?.ctInfo, this.tenantService.isCXOne),
      ctComparator(this.planDetail?.ctInfo, this.tenantService.isCXOne));
    this.applyGroupState();
  }

  /** Pinned and scrolling rows together, as displayed top to bottom. */
  get displayedRows(): ComparisonRow[] {
    return [...this.pinnedRows, ...this.rowData];
  }

  private applyGroupState() {
    this.pinnedRows = this.allRows.slice(0, PINNED_ROW_COUNT);
    this.rowData = this.allRows.slice(PINNED_ROW_COUNT).filter(r => !r.parentKey || !this.collapsedGroups.has(r.parentKey));
  }

  private toggleGroup(groupKey: string, params: ICellRendererParams) {
    if (!this.collapsedGroups.delete(groupKey)) {
      this.collapsedGroups.add(groupKey);
    }
    this.applyGroupState();
    params.api.redrawRows({rowNodes: [params.node]});
  }

  /** Full-width group header: same expand/collapse affordance and layout as the Plan Detail MU rollup header. */
  private groupHeaderRenderer(params: ICellRendererParams): HTMLElement {
    const groupKey: string = params.data.groupKey;
    const collapsed = this.collapsedGroups.has(groupKey);
    const wrapper = document.createElement("div");
    wrapper.className = "ef-group-header";
    wrapper.tabIndex = 0;
    wrapper.setAttribute("role", "button");
    wrapper.setAttribute("aria-expanded", String(!collapsed));
    wrapper.innerHTML = `<span class="ag-group-${collapsed ? "contracted" : "expanded"}">` +
      `<span class="ag-icon ag-icon-tree-${collapsed ? "closed" : "open"}"></span></span>`;
    const text = document.createElement("span");
    text.className = "ef-group-label";
    text.textContent = params.data.label ?? "";
    text.title = params.data.label ?? "";
    wrapper.appendChild(text);
    wrapper.addEventListener("click", () => this.toggleGroup(groupKey, params));
    wrapper.addEventListener("keydown", (event: KeyboardEvent) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        this.toggleGroup(groupKey, params);
      }
    });
    return wrapper;
  }

  private buildColumnDefs(year: ExternalForecastYear): ColDef[] {
    const format = (value: number, row?: ComparisonRow) => value === null || value === undefined ? ""
      : row?.format === "percent" ? formatPercent(value, this.locale) : formatCount(value, this.locale);
    // Like Plan Detail, headers carry tooltips; only the Parameter cells do (value cells have none).
    const parameterHeader = this.translate.instant("plan.label.param");
    const totalHeader = this.translate.instant("plan.label.total");
    const averageHeader = this.translate.instant("plan.label.average");
    // Average stays read-only and keeps the Phase 1 classes; the Business Metric average is calculated, so blue.
    const summaryCellClass = (params: CellClassParams<ComparisonRow>) => {
      const classes = params.data?.calculated || params.data?.cellKey === "BUSINESS_METRIC"
        ? [SUMMARY_CELL_CLASS, CALCULATED_CELL_CLASS] : SUMMARY_CELL_CLASS;
      // Red like the month and Total cells; it wins over the blue (grid.scss) and the dark summary text.
      return params.data?.average != null && params.data.average < 0 ? [].concat(classes, "negative-value") : classes;
    };
    return [
      {
        colId: ROW_HEADER_COL_ID,
        headerName: parameterHeader,
        headerTooltip: parameterHeader,
        field: "label",
        tooltipField: "label",
        pinned: "left",
        width: PARAMETER_COL_WIDTH,
        suppressSizeToFit: true,
        cellClass: params => params.data?.labelBold ? "ef-row-header ef-label-bold" : "ef-row-header",
        // Inline so no global grid rule can override the emphasis (same dark text as the period headers).
        cellStyle: params => ({
          ...(params.data?.labelBold ? {fontWeight: "bold", color: "rgb(46, 46, 46)"} : {}),
          ...(params.data?.parentKey ? {paddingLeft: "22px"} : {})
        })
      },
      ...year.periods.map((period): ColDef => {
        const header = DateUtils.getMonthYear(period.period, this.dateLocale);
        return {
          colId: period.period,
          headerName: header,
          headerTooltip: header,
          headerClass: DARK_HEADER_CLASS,
          valueGetter: params => params.data?.values[period.period],
          valueFormatter: params => format(params.value, params.data),
          ...this.editColumn(year, period),
          type: "rightAligned"
        };
      }),
      {
        colId: TOTAL_COL_ID,
        headerName: totalHeader,
        headerTooltip: totalHeader,
        headerClass: DARK_HEADER_CLASS,
        field: "total",
        ...this.editColumn(year, null),
        // not a fill source: a drag can never start from or carry the annual Total
        suppressFillHandle: true,
        cellClass: params => [SUMMARY_CELL_CLASS, ...this.valueCellClasses(params, null)],
        // Anchored to the grid's right edge; spare width stays blank between the last month and Total.
        pinned: "right",
        valueFormatter: params => format(params.value, params.data),
        type: "rightAligned"
      },
      {
        colId: AVERAGE_COL_ID,
        headerName: averageHeader,
        headerTooltip: averageHeader,
        headerClass: DARK_HEADER_CLASS,
        // Pinned right after Total so both stay visible while the months scroll.
        pinned: "right",
        cellClass: summaryCellClass,
        field: "average",
        valueFormatter: params => format(params.value, params.data),
        type: "rightAligned"
      }
    ];
  }
  /**
   * Editing for a month (period) or the Total column (null), as Plan Detail: editable only where the response's
   * cellMeta / pctMeta says EDITED and no call is in flight; the shared numeric editor without incremental edits but
   * with typed negatives; the typed value is written into the row and recorded by onCellValueChanged.
   */
  private editColumn(year: ExternalForecastYear, period: ExternalForecastPeriod | null): Partial<ColDef<ComparisonRow>> {
    const colId = period ? period.period : TOTAL_COL_ID;
    return {
      editable: (params: EditableCallbackParams<ComparisonRow>) =>
        !!params.data && !this.callInFlight && !this.noValidForecast && isEditableCell(params.data, period, year)
        && this.isUnlocked(this.cellId(params.data, period?.period ?? null)),
      // CT shares are month-only: their month cells show the total indicator (Staffing Distribution's error icon)
      ...(period ? {
        cellRendererSelector: (params: ICellRendererParams<ComparisonRow>) => params.data?.ctShare
          ? {component: CtTotalIndicatorComponent, params: {locale: this.locale}} : undefined
      } : {}),
      cellEditor: "numericEditor",
      // a fill hands the valueSetter ag-grid's computed number as is; its default parser would turn it into text, read as invalid
      useValueParserForImport: false,
      cellEditorParams: (params: ICellEditorParams<ComparisonRow>) => {
        const meta = cellMetaOf(params.data, period, year);
        return {
          origValue: params.value ?? null,
          locale: this.locale,
          allowNegative: true,
          metadata: {
            decimal: EDIT_DECIMALS,
            cellmetadata: {[colId]: meta},
            validator: {
              isValid: (text: unknown) => isValidValue(editorTextValue(text), meta),
              title: "validation.err.title",
              msg: this.rangeMessage(meta)
            }
          }
        };
      },
      valueSetter: (params: ValueSetterParams<ComparisonRow>) => {
        const typed = toCellValue(params.newValue);
        // a CT share keeps 2 decimals, truncated as in Plan Detail and Staffing Distribution (typed and pasted alike)
        const value = period && params.data.ctShare && typed !== null ? truncateDecimals(typed) : typed;
        const current = period ? params.data.values[period.period] : params.data.total;
        if (Object.is(value, current ?? null)) {
          return false;
        }
        if (period) {
          params.data.values[period.period] = value;
        } else {
          params.data.total = value;
        }
        return true;
      },
      cellClass: (params: CellClassParams<ComparisonRow>) => {
        const classes = this.valueCellClasses(params, period);
        // ag-grid appends cells in creation order, so grid.scss's ":last-child" border can land on another month.
        return period && period.period === year.periods[year.periods.length - 1]?.period
          ? [...classes, LAST_MONTH_CELL_CLASS] : classes;
      }
    };
  }

  /**
   * As Plan Detail: while a cell is invalid, only the held batch and the invalid cells (which include every open CT
   * share of an invalid month) can be edited; the faded cells are locked.
   */
  private isUnlocked(id: string): boolean {
    return this.invalidCells.size === 0 || this.pendingChanges.has(id) || this.invalidCells.has(id);
  }

  /** Phase 1 classes plus Plan Detail's: blue / hatched / italic / red negatives, red invalid, faded while locked. */
  private valueCellClasses(params: CellClassParams<ComparisonRow>, period: ExternalForecastPeriod | null): string[] {
    const row = params.data;
    if (!row) {
      return [];
    }
    const classes = cellStyleClasses(row, period);
    if (this.invalidCells.size > 0) {
      const id = this.cellId(row, period?.period ?? null);
      // As Plan Detail: the invalid cell is red; every cell outside the held-back batch is faded.
      classes.push(this.invalidCells.has(id) ? "invalid" : this.pendingChanges.has(id) ? "" : "locked");
    }
    return classes.filter(Boolean);
  }

  /** The editor's range message (spec 8.1): "between {0} and {1}", or "{0} or more" when there is no maximum. */
  private rangeMessage(meta: CellMeta | null): string {
    const bound = (value: number) => new Intl.NumberFormat(this.locale, {maximumFractionDigits: EDIT_DECIMALS}).format(value);
    if (meta?.min == null) {
      return "";
    }
    const fill = (key: string, ...values: number[]) =>
      values.reduce((text, value, i) => text.replace(`{${i}}`, bound(value)), this.translate.instant(key) as string);
    return meta.max == null
      ? fill("external.forecast.validation.min", meta.min)
      : fill("external.forecast.validation.range", meta.min, meta.max);
  }
}
